import type { GvgPlannerStoreComposition } from './gvgPlannerStoreComposition'
import { resolveLivePlayerForRaidPlayer } from '../lib/raidPlayerLiveLink'
import { fullPlayerInsertIndex, mergeVisiblePlayerOrder } from '../lib/guildPlayerFilter'

export function createGvgPlannerStorePublicApi(
  composition: GvgPlannerStoreComposition,
) {
  const {
    board,
    raid,
    tag,
    buildPreset,
    session,
    notificationSettings,
    loading,
    error,
    liveSyncError,
    fetchBoardFn,
    calendarWeekComputed,
    calendarActions,
    sessionActions,
    tagActions,
    tagAssignmentActions,
    tagResetActions,
    raidLifecycleActions,
    raidActions,
    stageActions,
    boardActions,
    groupRoomActions,
    weaponActions,
    playerBuildsActions,
    myBuildsActions,
    buildPresetActions,
    optimisticMoves,
    raidMoves,
    raidRsvpActions,
    locale,
    raidPlayerBuildsActions,
    raidMap,
    forestSpawnSlotsComputed,
    playerById,
    groupById,
    playersInGroup,
    roomGroups,
    roomWeaponCounts,
    counts,
    currentRooms,
    currentGroups,
    currentPlayers,
    livePlayersByDiscordId,
    tagById,
    tagsForPlayer,
    playerCountForTag,
    raidStatusFn,
    activeRaidEditLocked,
    refreshActiveRaidEditLock,
    clearLiveSyncError,
    FOREST_LOCATIONS,
    raidPlayerToPlayer,
    raidRoomToRoom,
    raidGroupToGroup,
  } = composition

  function fullGroupOrder(groupId: string): ReadonlyArray<string> {
    if (raid.activeRaidId.value === null) {
      return board.groups.value.find((group) => group.id === groupId)?.playerIds ?? []
    }
    const group = raid.raidGroups.value.find((entry) => (entry.stableId || entry.id) === groupId)
    return group === undefined ? [] : raidGroupToGroup(group).playerIds
  }

  // state
  return {
    players: board.players,
    groups: board.groups,
    rooms: board.rooms,
    loading,
    error,
    liveSyncError,
    clearLiveSyncError,
    guildsLoading: session.guildsLoading,
    availableGuilds: session.availableGuilds,
    activeDiscordGuildId: session.activeDiscordGuildId,
    dbGuildId: session.dbGuildId,
    ...notificationSettings,
    /**
     * Application locale (`AppLocale`) — sourced from the host-owned
     * `useI18n().locale` computed. Forwarded through the composer so
     * out-of-store consumers (calendar overlays, locale-aware label
     * helpers, the `LanguageSelector` route) can read the active locale
     * without re-injecting the `useI18n` composable. The calendar week
     * math (`buildRaidCalendarWeek` + `applyPlannerState`) and the
     * liveSync window trim already consume this same ref internally,
     * so the public export shares the reactive identity — no snapshot
     * is captured at setup time.
     */
    locale,
    // raid / stage / tag state
    raids: raid.raids,
    activeRaidId: raid.activeRaidId,
    activeRaidBoard: raid.activeRaidBoard,
    boardMode: raid.boardMode,
    raidRooms: raid.raidRooms,
    raidGroups: raid.raidGroups,
    raidPlayers: raid.raidPlayers,
    raidStages: raid.raidStages,
    raidStageDiffs: raid.raidStageDiffs,
    activeStageId: raid.activeStageId,
    stageSelectionPending: raid.stageSelectionPending,
    raidMapGroupPositions: raidMap.groupPositions,
    raidMapTreePosition: raidMap.treePosition,
    raidForestAssignments: raidMap.forestAssignments,
    raidMapLoading: raidMap.loading,
    raidMapError: raidMap.error,
    forestLocations: FOREST_LOCATIONS,
    forestSpawnSlots: forestSpawnSlotsComputed,
    playerTags: tag.playerTags,
    playerBuildPresetOwners: buildPreset.playerBuildPresetOwners,
    guildBuildPresets: buildPreset.guildBuildPresets,
    activeTagFilter: tag.activeTagFilter,
    calendarViewOpen: raid.calendarViewOpen,
    calendarWindowStart: raid.calendarWindowStart,
    raidsForWindow: raid.raidsForWindow,
    calendarWeek: calendarWeekComputed,
    // getters
    viewMode: board.viewMode,
    playerNameMode: board.playerNameMode,
    playerById,
    groupById,
    playersInGroup,
    roomGroups,
    roomWeaponCounts,
    counts,
    isMember: session.isMember,
    isOfficer: session.isOfficer,
    isOwner: session.isOwner,
    canEditHistoricalRaids: session.canEditHistoricalRaids,
    activeGuildOwnerDiscordId: session.activeGuildOwnerDiscordId,
    currentRooms,
    currentGroups,
    currentPlayers,
    tagById,
    tagsForPlayer,
    playerCountForTag,
    // adapters
    raidPlayerToPlayer,
    raidRoomToRoom,
    raidGroupToGroup,
    // status helper
    raidStatus: raidStatusFn,
    activeRaidEditLocked,
    refreshActiveRaidEditLock,
    // data
    fetchBoard: () => fetchBoardFn(),
    loadAvailableGuilds: sessionActions.loadAvailableGuilds,
    resolveDbGuildId: sessionActions.resolveDbGuildId,
    setActiveDiscordGuildId: sessionActions.setActiveDiscordGuildId,
    // tag CRUD
    fetchPlayerTags: tagActions.fetchPlayerTags,
    createTag: tagActions.createTag,
    renameTag: tagActions.renameTag,
    setTagColor: tagActions.setTagColor,
    deleteTag: tagActions.deleteTag,
    assignTag: tagAssignmentActions.assignTag,
    unassignTag: tagAssignmentActions.unassignTag,
    resetAllTags: tagResetActions.resetAllTags,
    isActiveTagFilter: tagAssignmentActions.isActiveTagFilter,
    setActiveTagFilter: tagAssignmentActions.setActiveTagFilter,
    // raid CRUD
    loadRaids: raidActions.loadRaids,
    selectRaid: raidLifecycleActions.selectRaid,
    createRaid: raidActions.createRaid,
    createRaidWithSchedule: raidActions.createRaidWithSchedule,
    rescheduleRaid: raidActions.rescheduleRaid,
    scheduleRaid: raidActions.scheduleRaid,
    cancelRaid: raidActions.cancelRaid,
    setRaidPublished: raidActions.setRaidPublished,
    updateRaid: raidActions.updateRaid,
    deleteRaid: raidActions.deleteRaid,
    applyRaidToBoard: raidActions.applyRaidToBoard,
    setRaidRsvp: raidRsvpActions.setRaidRsvp,
    // tactical map + forest rotations
    fetchRaidMapPlan: raidMap.fetchRaidMapPlan,
    setRaidMapGroupPosition: raidMap.setRaidMapGroupPosition,
    setRaidMapTreePosition: raidMap.setRaidMapTreePosition,
    upsertRaidForestAssignment: raidMap.upsertRaidForestAssignment,
    removeRaidForestAssignment: raidMap.removeRaidForestAssignment,
    // calendar
    loadRaidsForWindow: calendarActions.loadRaidsForWindow,
    refreshCalendarWindow: calendarActions.refreshCalendarWindow,
    setCalendarWindowStart: calendarActions.setCalendarWindowStart,
    goToCurrentWeek: calendarActions.goToCurrentWeek,
    // stage CRUD
    loadStagesForRaid: stageActions.loadStagesForRaid,
    selectStage: stageActions.selectStage,
    addStage: stageActions.addStage,
    updateStage: stageActions.updateStage,
    removeStage: stageActions.removeStage,
    reorderStages: stageActions.reorderStages,
    resetStageChanges: stageActions.resetStageChanges,
    resetAllStageChanges: stageActions.resetAllStageChanges,
    // player editing
    cycleRole: weaponActions.cycleRole,
    renamePlayer: weaponActions.renamePlayer,
    setMainWeapon: weaponActions.setMainWeapon,
    setAltWeapon: weaponActions.setAltWeapon,
    addMainSlot: weaponActions.addMainSlot,
    addAltPair: weaponActions.addAltPair,
    clearAlt: weaponActions.clearAlt,
    // saved builds
    addBuild: playerBuildsActions.addBuild,
    removeBuild: playerBuildsActions.removeBuild,
    setBuild: playerBuildsActions.setBuild,
    addGuildBuild: playerBuildsActions.addGuildBuild,
    setGuildBuild: playerBuildsActions.setGuildBuild,
    removeGuildBuild: playerBuildsActions.removeGuildBuild,
    assignGuildBuild: (id: string, buildId: string): void => {
      if (raid.activeRaidId.value === null) {
        playerBuildsActions.assignGuildBuild(id, buildId)
        return
      }
      const raidPlayer = raid.raidPlayers.value.find((player) => player.id === id)
      const livePlayer = raidPlayer === undefined
        ? undefined
        : resolveLivePlayerForRaidPlayer(
            raidPlayer,
            board.players.value,
            livePlayersByDiscordId.value,
          )
      if (livePlayer === undefined) {
        error.value = 'This raid player is no longer linked to the live guild roster.'
        return
      }
      playerBuildsActions.assignGuildBuild(livePlayer.id, buildId)
    },
    unassignGuildBuild: playerBuildsActions.unassignGuildBuild,
    setActiveGuildBuild: (id: string, buildId: string): void => {
      if (raid.activeRaidId.value !== null) {
        // Resolve against the same projected player that rendered the popup.
        // A snapshot row can still point at its live player by source id even
        // when its copied Discord identity is stale, so a second Discord-map
        // lookup could reject a build that is visibly selectable.
        const guildBuild = currentPlayers.value
          .find((player) => player.id === id)
          ?.guildBuilds.find((build) => build.id === buildId)
        if (guildBuild === undefined) {
          error.value = 'That guild build is no longer available.'
          return
        }
        error.value = null
        void raidPlayerBuildsActions.setActiveBuildPair(id, guildBuild.weaponSlugs)
        return
      }
      playerBuildsActions.setActiveGuildBuild(id, buildId)
    },
    setActiveBuild: (id: string, index: number): void => {
      // Raid-mode build selection addresses a SNAPSHOT row id, which the live
      // `playerSetActiveBuild` mutation cannot target — route through the
      // snapshot-scoped `raidPlayerReplaceBuilds` instead. Mirrors the
      // `movePlayerToGroup` / `reorderGroup` raid/live router above.
      if (raid.activeRaidId.value !== null) {
        raidPlayerBuildsActions.setActiveBuild(id, index)
        return
      }
      playerBuildsActions.setActiveBuild(id, index)
    },
    setRaidPlayerBuildPair: (
      id: string,
      weapons: ReadonlyArray<string>,
    ): Promise<boolean> => {
      if (raid.activeRaidId.value === null) {
        error.value = 'A raid must be selected before choosing its player build.'
        return Promise.resolve(false)
      }
      return raidPlayerBuildsActions.setActiveBuildPair(id, weapons)
    },
    clearBuilds: playerBuildsActions.clearBuilds,
    // my-builds (cross-server, plan my-builds) — operates on the signed-in
    // player's saved build library by Discord id. Mutations go through
    // the new discord-keyed `my*Build*` mutations so the player can
    // edit their library from `/my-builds` regardless of which guild
    // board they currently have mounted (see `stores/board/myBuilds.ts`).
    // `myBuilds` mirrors the response of the
    // `myPlayerBuilds(discordUserId:)` query — each row is the build's
    // `orderIndex` paired with its `weaponSlugs`.
    myBuilds: myBuildsActions.builds,
    myBuildsLoading: myBuildsActions.loading,
    myBuildsError: myBuildsActions.error,
    fetchMyBuilds: myBuildsActions.fetchMyBuilds,
    addMyBuild: myBuildsActions.addMyBuild,
    setMyBuild: myBuildsActions.setMyBuild,
    removeMyBuild: myBuildsActions.removeMyBuild,
    setMyActiveBuild: myBuildsActions.setMyActiveBuild,
    setMyDefaultBuild: myBuildsActions.setMyDefaultBuild,
    // stage build presets
    myBuildPresets: buildPreset.myBuildPresets,
    myBuildPresetsLoading: buildPreset.myBuildPresetsLoading,
    myBuildPresetsError: buildPreset.myBuildPresetsError,
    fetchMyBuildPresets: buildPresetActions.fetchMyBuildPresets,
    createMyBuildPreset: buildPresetActions.createMyBuildPreset,
    updateMyBuildPreset: buildPresetActions.updateMyBuildPreset,
    removeMyBuildPreset: buildPresetActions.removeMyBuildPreset,
    createGuildBuildPreset: buildPresetActions.createGuildBuildPreset,
    updateGuildBuildPreset: buildPresetActions.updateGuildBuildPreset,
    removeGuildBuildPreset: buildPresetActions.removeGuildBuildPreset,
    applyBuildPreset: buildPresetActions.applyBuildPreset,
    // group management
    addGroup: groupRoomActions.addGroup,
    removeGroup: groupRoomActions.removeGroup,
    renameGroup: groupRoomActions.renameGroup,
    setGroupCollapsed: (groupId: string, collapsed: boolean): void => {
      if (raid.activeRaidId.value === null) {
        groupRoomActions.setGroupCollapsed(groupId, collapsed)
        return
      }
      void raidMoves.setGroupCollapsed(groupId, collapsed)
    },
    moveRoom: (roomId: string, targetIndex: number): void => {
      if (raid.activeRaidId.value === null) {
        // Live-mode call still returns the optimistic rollback object —
        // callers `void` it (`PlayerGroup.vue`, `RoomSection.vue`).
        optimisticMoves.moveRoom(roomId, targetIndex)
        return
      }
      void raidMoves.moveRoom(roomId, targetIndex)
    },
    setMaxPlayers: groupRoomActions.setMaxPlayers,
    // room lifecycle
    addRoom: boardActions.addRoom,
    removeRoom: boardActions.removeRoom,
    renameRoom: boardActions.renameRoom,
    // player lifecycle
    addNewPlayer: boardActions.addNewPlayer,
    addGuildMember: boardActions.addGuildMember,
    removePlayer: boardActions.removePlayer,
    // officer management (owner-only)
    setGuildOfficer: sessionActions.setGuildOfficer,
    fetchOfficerRoster: boardActions.fetchOfficerRoster,
    // drag & drop
    movePlayerToGroup: async (playerId: string, targetGroupId: string, index: number): Promise<boolean> => {
      if (board.guildPlayerDiscordIds.value !== null) {
        index = fullPlayerInsertIndex(
          fullGroupOrder(targetGroupId),
          new Set(currentPlayers.value.map((player) => player.id)),
          playerId,
          index,
        )
      }
      if (raid.activeRaidId.value === null) {
        return optimisticMoves.movePlayerToGroup(playerId, targetGroupId, index).result
      }
      return raidMoves.movePlayerToGroup(playerId, targetGroupId, index)
    },
    reorderGroup: (groupId: string, orderedPlayerIds: ReadonlyArray<string>): void => {
      if (board.guildPlayerDiscordIds.value !== null) {
        orderedPlayerIds = mergeVisiblePlayerOrder(
          fullGroupOrder(groupId),
          orderedPlayerIds,
          new Set(currentPlayers.value.map((player) => player.id)),
        )
      }
      if (raid.activeRaidId.value === null) {
        optimisticMoves.reorderGroup(groupId, orderedPlayerIds)
        return
      }
      void raidMoves.reorderGroup(groupId, orderedPlayerIds)
    },
    sortGroupByRole: optimisticMoves.sortGroupByRole,
    sortAllGroups: optimisticMoves.sortAllGroups,
    // view
    setViewMode: board.persistViewMode,
    setPlayerNameMode: board.persistPlayerNameMode,
    // bulk
    reset: boardActions.reset,
    exportSnapshot: boardActions.exportSnapshot,
  }
}
